import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Search,
  Filter,
  ArrowRight,
  FileText,
  Upload,
  CheckCheck,
  AlertOctagon,
  Clock
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { UploadPOForm } from "@/components/po/UploadPOForm";
import { PurchaseOrder } from "@/lib/types";

interface ValidationIssue {
  id: string;
  poNumber: string;
  field: string;
  description: string;
  severity: "Error" | "Warning" | "Info";
  suggestedFix?: string;
}

const POValidation = () => {
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTab, setCurrentTab] = useState("pending");
  const { toast } = useToast();

  // Query for purchase orders
  const { data: purchaseOrdersData, isLoading, refetch } = useQuery({
    queryKey: ["/api/purchase-orders/validation", { search: searchTerm, filter: currentTab }],
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Trigger refetch with new search term
    refetch();
  };

  const handleTabChange = (value: string) => {
    setCurrentTab(value);
    // Trigger refetch with new filter
    refetch();
  };

  const handleApprove = async (poId: number) => {
    try {
      await apiRequest("PATCH", `/api/purchase-orders/${poId}/approve`, {});
      toast({
        title: "PO Approved",
        description: "The purchase order has been approved and moved to inventory check",
        variant: "default",
      });
      refetch();
    } catch (error) {
      toast({
        title: "Approval Failed",
        description: error instanceof Error ? error.message : "An error occurred during approval",
        variant: "destructive",
      });
    }
  };

  const handleReject = async (poId: number) => {
    try {
      await apiRequest("PATCH", `/api/purchase-orders/${poId}/reject`, {});
      toast({
        title: "PO Rejected",
        description: "The purchase order has been rejected",
        variant: "default",
      });
      refetch();
    } catch (error) {
      toast({
        title: "Rejection Failed",
        description: error instanceof Error ? error.message : "An error occurred during rejection",
        variant: "destructive",
      });
    }
  };

  const handleViewDetails = (poId: number) => {
    setLocation(`/po/${poId}`);
  };

  // Mock data
  const mockPurchaseOrders: PurchaseOrder[] = [
    {
      id: 3,
      poNumber: "PO-23454",
      brand: "Puma",
      category: "Accessories",
      items: 32,
      totalValue: "$6,240.00",
      status: "Validation Pending",
      createdAt: "2023-06-08",
      updatedAt: "2023-06-08",
      expectedDelivery: "2023-06-28",
    },
    {
      id: 4,
      poNumber: "PO-23451",
      brand: "Nike",
      category: "Equipment",
      items: 12,
      totalValue: "$3,850.00",
      status: "Validation Pending",
      createdAt: "2023-06-05",
      updatedAt: "2023-06-05",
      expectedDelivery: "2023-06-22",
    },
    {
      id: 5,
      poNumber: "PO-23450",
      brand: "Adidas",
      category: "Apparel",
      items: 28,
      totalValue: "$8,120.00",
      status: "Validation Pending",
      createdAt: "2023-06-04",
      updatedAt: "2023-06-04",
      expectedDelivery: "2023-06-21",
    },
    {
      id: 6,
      poNumber: "PO-23449",
      brand: "Under Armour",
      category: "Footwear",
      items: 18,
      totalValue: "$5,430.00",
      status: "Validation Failed",
      createdAt: "2023-06-03",
      updatedAt: "2023-06-04",
      expectedDelivery: "2023-06-20",
    },
    {
      id: 7,
      poNumber: "PO-23448",
      brand: "Reebok",
      category: "Accessories",
      items: 22,
      totalValue: "$4,180.00",
      status: "Validated",
      createdAt: "2023-06-02",
      updatedAt: "2023-06-03",
      expectedDelivery: "2023-06-19",
    },
  ];

  const mockValidationIssues: ValidationIssue[] = [
    {
      id: "issue1",
      poNumber: "PO-23454",
      field: "HSN Code",
      description: "Missing or invalid HSN code for 5 items",
      severity: "Error",
      suggestedFix: "Update HSN code to match product category",
    },
    {
      id: "issue2",
      poNumber: "PO-23454",
      field: "Price",
      description: "Unit price variance exceeds 10% threshold for 'Item #A122'",
      severity: "Warning",
      suggestedFix: "Review pricing with supplier",
    },
    {
      id: "issue3",
      poNumber: "PO-23451",
      field: "Quantity",
      description: "Order quantity below minimum order quantity for 'Item #N305'",
      severity: "Warning",
    },
    {
      id: "issue4",
      poNumber: "PO-23450",
      field: "Supplier Code",
      description: "Supplier code doesn't match registered code in system",
      severity: "Error",
      suggestedFix: "Update supplier code to 'ADS-2301'",
    },
    {
      id: "issue5",
      poNumber: "PO-23449",
      field: "Multiple",
      description: "Multiple critical issues detected - see detailed report",
      severity: "Error",
    },
  ];

  // Use real data if available, otherwise use mock data
  const purchaseOrders = (purchaseOrdersData as any )?.items || mockPurchaseOrders;
  const validationIssues = mockValidationIssues;
  
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Validated":
        return "success";
      case "Validation Pending":
        return "warning";
      case "Validation Failed":
        return "destructive";
      default:
        return "pending";
    }
  };

  const getSeverityBadgeVariant = (severity: string) => {
    switch (severity) {
      case "Error":
        return "destructive";
      case "Warning":
        return "warning";
      case "Info":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <main className="p-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-gray-900">PO Validation</h1>
          <p className="mt-1 text-sm text-gray-600">Review and validate purchase orders before inventory allocation</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="flex items-center">
                <Upload className="mr-2 h-4 w-4" />
                Upload New Master
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <UploadPOForm />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b border-gray-200 flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
          <CardTitle className="text-lg font-medium text-gray-900">Purchase Orders</CardTitle>
          <div className="flex w-full sm:w-auto space-x-2">
            <form onSubmit={handleSearch} className="relative flex-1 sm:flex-none">
              <Input
                type="text"
                placeholder="Search POs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-8 w-full sm:w-64"
              />
              <button type="submit" className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </button>
            </form>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => {
                toast({
                  title: "Filter applied",
                  description: "Purchase orders have been filtered",
                });
              }}
              className="flex-shrink-0"
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs defaultValue="pending" onValueChange={handleTabChange}>
            <div className="px-4 pt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="pending" className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  Pending
                </TabsTrigger>
                <TabsTrigger value="validated" className="flex items-center">
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Validated
                </TabsTrigger>
                <TabsTrigger value="failed" className="flex items-center">
                  <AlertOctagon className="h-4 w-4 mr-2" />
                  Failed
                </TabsTrigger>
                <TabsTrigger value="all" className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  All POs
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="pending" className="mt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>PO Number</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issues</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">
                          Loading purchase orders...
                        </TableCell>
                      </TableRow>
                    ) : purchaseOrders.filter(po => po.status === "Validation Pending").length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">
                          No pending purchase orders found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      purchaseOrders.filter(po => po.status === "Validation Pending").map((po) => {
                        const poIssues = validationIssues.filter(issue => issue.poNumber === po.poNumber);
                        const hasErrors = poIssues.some(issue => issue.severity === "Error");
                        
                        return (
                          <TableRow key={po.id} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-primary">{po.poNumber}</TableCell>
                            <TableCell>{po.brand}</TableCell>
                            <TableCell>{po.category}</TableCell>
                            <TableCell>{po.items}</TableCell>
                            <TableCell>{po.totalValue}</TableCell>
                            <TableCell>
                              <Badge variant={getStatusBadgeVariant(po.status)}>
                                {po.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {poIssues.length > 0 ? (
                                <div className="flex items-center">
                                  {hasErrors ? (
                                    <AlertOctagon className="h-4 w-4 text-destructive mr-1" />
                                  ) : (
                                    <AlertTriangle className="h-4 w-4 text-warning mr-1" />
                                  )}
                                  {poIssues.length} {poIssues.length === 1 ? "issue" : "issues"}
                                </div>
                              ) : (
                                <div className="flex items-center text-success">
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  No issues
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex space-x-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                                  onClick={() => handleReject(po.id)}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-success border-success hover:bg-success hover:text-white"
                                  onClick={() => handleApprove(po.id)}
                                  disabled={hasErrors}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDetails(po.id)}
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Validation Issues Section */}
              {validationIssues.length > 0 && (
                <div className="p-4 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Validation Issues</h3>
                  <div className="space-y-4">
                    {validationIssues.map((issue) => (
                      <div key={issue.id} className="p-3 border rounded-md bg-accent">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center">
                              <Badge variant={getSeverityBadgeVariant(issue.severity)} className="mr-2">
                                {issue.severity}
                              </Badge>
                              <span className="font-medium">{issue.poNumber}</span>
                            </div>
                            <div className="mt-1 text-sm">
                              <span className="font-medium">{issue.field}:</span> {issue.description}
                            </div>
                            {issue.suggestedFix && (
                              <div className="mt-1 text-sm text-success">
                                <span className="font-medium">Suggested Fix:</span> {issue.suggestedFix}
                              </div>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewDetails(purchaseOrders.find(po => po.poNumber === issue.poNumber)?.id || "")}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Other tabs would follow similar patterns but with different filtered data */}
            <TabsContent value="validated" className="mt-0">
              {/* Similar table structure */}
            </TabsContent>
            <TabsContent value="failed" className="mt-0">
              {/* Similar table structure */}
            </TabsContent>
            <TabsContent value="all" className="mt-0">
              {/* Similar table structure but with all POs */}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </main>
  );
};

export default POValidation;
